import React, { useState, useEffect } from 'react';
import axios from 'axios';

const FinOpsOrganizationListView = ({ organizations, onOrgSelect, standaloneAccountCount = 0, selectedMonth, tenantID }) => {
  const [orgMetrics, setOrgMetrics] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOrgMetrics();
  }, [organizations, selectedMonth]);

  const loadOrgMetrics = async () => {
    try {
      const tenantId = tenantID || localStorage.getItem('tenantID');
      const organizationId = localStorage.getItem('organizationID');
      const userId = tenantId || organizationId;
      
      if (!userId) {
        console.error('No tenantId or organizationID found');
        setLoading(false);
        return;
      }

      const API_URL = tenantId 
        ? 'https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod'
        : 'https://49pgt66j8g.execute-api.us-west-2.amazonaws.com/prod';
      
      // Fetch aggregates for trend calculation
      let aggregates = [];
      if (tenantId) {
        try {
          const aggResponse = await fetch(`${API_URL}/v2/finops/aggregates?tenantID=${tenantId}&months=5`);
          if (aggResponse.ok) {
            const aggData = await aggResponse.json();
            aggregates = aggData.aggregates || [];
          }
        } catch (err) {
          console.error('Failed to fetch aggregates:', err);
        }
      }
      
      const metrics = {};
      for (const org of organizations) {
        const memberAccounts = org.memberAccounts || [];
        const accountCount = memberAccounts.length + 1;
        const connectedCount = memberAccounts.filter(acc => acc.connected).length + 1;
        
        // Calculate trend from aggregates
        const orgAccounts = [org.id, ...memberAccounts.map(m => m.accountId)];
        const monthlyTotals = {};
        aggregates.filter(a => orgAccounts.includes(a.accountId)).forEach(agg => {
          if (!monthlyTotals[agg.month]) monthlyTotals[agg.month] = 0;
          monthlyTotals[agg.month] += parseFloat(agg.totalCost || 0);
        });
        const months = Object.keys(monthlyTotals).sort();
        const currentMonth = months[months.length - 1];
        const previousMonth = months[months.length - 2];
        const trend = previousMonth ? ((monthlyTotals[currentMonth] - monthlyTotals[previousMonth]) / monthlyTotals[previousMonth] * 100) : null;
        
        // Query Cost Explorer for this specific org
        try {
          console.log(`Fetching cost for org ${org.orgId}`);
          let url = tenantId 
            ? `${API_URL}/v2/finops/dashboard?tenantID=${tenantId}&orgId=${org.orgId}`
            : `${API_URL}/dashboard?organizationID=${organizationId}&orgId=${org.orgId}`;
          if (selectedMonth) url += `&month=${selectedMonth}`;
          const response = await fetch(url);
          
          let totalCost = 0;
          let usageCost = 0;
          let credits = 0;
          if (response.ok) {
            const orgData = await response.json();
            totalCost = orgData?.cost?.total ? parseFloat(orgData.cost.total) : 0;
            usageCost = orgData?.cost?.usage_cost ? parseFloat(orgData.cost.usage_cost) : 0;
            credits = orgData?.cost?.credits ? parseFloat(orgData.cost.credits) : 0;
            console.log(`Org ${org.orgId} cost from API:`, totalCost);
          } else {
            console.warn(`Failed to fetch cost for org ${org.orgId}`);
          }

          metrics[org.orgId] = {
            accountCount,
            connectedCount,
            totalCost,
            usageCost,
            credits,
            spCoverage: 0,
            trend
          };
        } catch (error) {
          console.error(`Error fetching cost for org ${org.orgId}:`, error);
          metrics[org.orgId] = {
            accountCount,
            connectedCount,
            totalCost: 0,
            spCoverage: 0,
            trend: null
          };
        }
      }
      
      setOrgMetrics(metrics);
      setLoading(false);
    } catch (error) {
      console.error('Error loading org metrics:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="row g-4">
      <div className="col-12">
        <div className="mb-4">
          <h4 className="mb-1">AWS Organizations & Accounts</h4>
          <p className="text-muted mb-0">Select an organization or standalone accounts to view detailed cost analysis</p>
        </div>
      </div>

      {/* Standalone Accounts Card */}
      {standaloneAccountCount > 0 && (
        <div className="col-md-6">
          <div 
            className="card shadow-sm border-0 h-100" 
            style={{ cursor: 'pointer', transition: 'all 0.2s' }}
            onClick={() => onOrgSelect(null)}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
            }}
          >
            <div className="card-body p-4">
              <div className="mb-3">
                <h5 className="mb-1" style={{ fontWeight: '600', color: '#1e293b' }}>
                  Standalone Accounts
                </h5>
                <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                  Individual AWS accounts not part of an organization
                </p>
              </div>

              <div className="row g-3 mb-3">
                <div className="col-6">
                  <div className="p-3 bg-light rounded">
                    <div className="d-flex align-items-center mb-1">
                      <svg width="16" height="16" fill="currentColor" className="me-2" viewBox="0 0 16 16">
                        <path d="M11 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0z"/>
                        <path d="M2 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2zm12 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1v-1c0-1-1-4-6-4s-6 3-6 4v1a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h12z"/>
                      </svg>
                      <small className="text-muted">Accounts</small>
                    </div>
                    <h4 className="mb-0" style={{ fontWeight: '700', color: '#342ead' }}>
                      {standaloneAccountCount}
                    </h4>
                  </div>
                </div>

                <div className="col-6">
                  <div className="p-3 bg-light rounded">
                    <div className="d-flex align-items-center mb-1">
                      <svg width="16" height="16" fill="currentColor" className="me-2" viewBox="0 0 16 16">
                        <path d="M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.05zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73l.348.086z"/>
                      </svg>
                      <small className="text-muted">Total Cost</small>
                    </div>
                    <h4 className="mb-0" style={{ fontWeight: '700', color: '#1e293b' }}>
                      N/A
                    </h4>
                    <small className="text-muted">Run scan to see costs</small>
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-end align-items-center pt-3 border-top">
                <button className="btn btn-sm" style={{ backgroundColor: '#342ead', color: 'white' }}>
                  View Details →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Organization Cards */}
      {organizations.map((org) => {
        const metrics = orgMetrics[org.orgId] || {};
        return (
          <div key={org.orgId} className="col-md-6">
            <div 
              className="card shadow-sm border-0 h-100" 
              style={{ cursor: 'pointer', transition: 'all 0.2s' }}
              onClick={() => onOrgSelect(org.orgId)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
              }}
            >
              <div className="card-body p-4">
                <div className="mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <img src="/organization.svg" alt="org" style={{ width: '24px', height: '24px' }} className="me-2" />
                    <h5 className="mb-0" style={{ fontWeight: '600', color: '#1e293b' }}>
                      {org.alias || 'AWS Organization'}
                    </h5>
                  </div>
                  <p className="text-muted mb-0" style={{ fontSize: '0.875rem', fontFamily: 'monospace' }}>
                    {org.orgId}
                  </p>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-6">
                    <div className="p-3 bg-light rounded">
                      <div className="d-flex align-items-center mb-1">
                        <svg width="16" height="16" fill="currentColor" className="me-2" viewBox="0 0 16 16">
                          <path d="M11 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0z"/>
                          <path d="M2 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2zm12 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1v-1c0-1-1-4-6-4s-6 3-6 4v1a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h12z"/>
                        </svg>
                        <small className="text-muted">Accounts</small>
                      </div>
                      <h4 className="mb-0" style={{ fontWeight: '700', color: '#342ead' }}>
                        {metrics.connectedCount || 0}
                      </h4>
                      <small className="text-muted">
                        {metrics.accountCount > metrics.connectedCount && (
                          <span className="text-warning">
                            {metrics.accountCount - metrics.connectedCount} not connected
                          </span>
                        )}
                        {metrics.accountCount === metrics.connectedCount && (
                          <span className="text-success">All connected ✓</span>
                        )}
                      </small>
                    </div>
                  </div>

                  <div className="col-6">
                    <div className="p-3 bg-light rounded">
                      <div className="d-flex align-items-center mb-1">
                        <svg width="16" height="16" fill="currentColor" className="me-2" viewBox="0 0 16 16">
                          <path d="M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.05zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73l.348.086z"/>
                        </svg>
                        <small className="text-muted">Net Cost</small>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <h4 className="mb-0" style={{ fontWeight: '700', color: '#1e293b' }}>
                          {metrics.totalCost !== undefined ? `$${metrics.totalCost.toFixed(2)}` : 'N/A'}
                        </h4>
                        {metrics.trend !== null && metrics.trend !== undefined && (
                          <span className={`badge ${metrics.trend > 0 ? 'bg-danger' : 'bg-success'}`} style={{ fontSize: '0.65rem' }}>
                            {metrics.trend > 0 ? '↑' : '↓'} {Math.abs(metrics.trend).toFixed(1)}%
                          </span>
                        )}
                      </div>
                      <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                        {metrics.usageCost !== undefined ? (
                          <span>Usage: ${metrics.usageCost.toFixed(2)} | Credits: ${metrics.credits.toFixed(2)}</span>
                        ) : 'All accounts'}
                      </small>
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-center pt-3 border-top">
                  <small className="text-muted">
                    Management: {org.id || 'N/A'}
                  </small>
                  <button className="btn btn-sm" style={{ backgroundColor: '#342ead', color: 'white' }}>
                    View Details →
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FinOpsOrganizationListView;
